'use memo';

import {
  AgxButton,
  AgxColumn,
  AgxDropdown,
  AgxGlobalSearch,
  AgxRow,
  AgxTypography,
  CampaignSearcherResult,
  DropdownOption,
  Icon_CheckmarkOutline,
  SearchResultType,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  UpdateCampaignStatusRequestStage,
  useGetUpdateCampaignStatusDetails,
  useInvalidateUseGetUpdateCampaignStatusDetails,
  useUpdateCampaignStatus,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useEffect, useMemo, useState } from 'react';
import SupportBackButton from '../SupportBackButton';

const UpdateCampaignStatus = () => {
  const { pushToast } = useToast();

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>();
  const [selectedStage, setSelectedStage] = useState<string>();

  const {
    data: campaignDetails,
    isPending: isLoadingCampaignDetails,
    isError: hasCampaignDetailsError,
  } = useGetUpdateCampaignStatusDetails(selectedCampaignId ?? '', !!selectedCampaignId);

  const invalidateUpdateCampaignStatusDetails = useInvalidateUseGetUpdateCampaignStatusDetails();

  const { updateCampaignStatus, updateCampaignStatusPending } = useUpdateCampaignStatus({
    onSuccess: async (returnData, inputData) => {
      if (inputData.campaignId) {
        await invalidateUpdateCampaignStatusDetails(inputData.campaignId);
      }

      setSelectedStage(returnData?.currentStage);
      pushToast({
        message: 'Campaign status updated',
        type: 'success',
      });
    },
    onError: () => {
      pushToast({
        message: 'Error updating campaign status',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    setSelectedStage(campaignDetails?.currentStage);
  }, [campaignDetails]);

  const onItemSelected = (itemType: SearchResultType, item: CampaignSearcherResult) => {
    if (
      itemType !== SearchResultType.ActiveCampaign &&
      itemType !== SearchResultType.ArchivedCampaign
    ) {
      return;
    }

    setSelectedCampaignId(item.campaignId);
    setSelectedStage(undefined);
  };

  const selectedCampaignDetails = useMemo(() => {
    if (!campaignDetails) return [];

    return [
      { label: 'Property', value: campaignDetails.propertyAddress },
      { label: 'Agency', value: campaignDetails.agencyName },
      { label: 'Current status', value: campaignDetails.currentStageLabel },
    ].filter((detail) => !!detail.value);
  }, [campaignDetails]);

  const stageOptions = useMemo<DropdownOption[]>(
    () =>
      (campaignDetails?.potentialStages ?? [])
        .filter((stageOption) => !!stageOption.stage && !!stageOption.label)
        .map((stageOption) => ({
          label: stageOption.label ?? '',
          value: stageOption.stage ?? '',
        })),
    [campaignDetails]
  );

  const currentStage = campaignDetails?.currentStage;
  const hasSelectedCampaign = !!selectedCampaignId;
  const hasChanged = !!selectedStage && selectedStage !== currentStage;

  const saveCampaignStatus = async () => {
    if (!campaignDetails?.campaignId || !selectedStage) return;

    await updateCampaignStatus({
      campaignId: campaignDetails.campaignId,
      stage: selectedStage as UpdateCampaignStatusRequestStage,
    });
  };

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel title="Update Campaign Status">
        <AgxColumn gap="spacing-xl" fill>
          <AgxGlobalSearch
            searchForCampaigns
            searchForArchivedCampaigns
            onItemSelected={onItemSelected}
          />

          {isLoadingCampaignDetails && hasSelectedCampaign && <LoadingState />}

          {hasCampaignDetailsError && (
            <AgxTypography id="update-campaign-status-error" type="body" size="small">
              Unable to load campaign details.
            </AgxTypography>
          )}

          {campaignDetails && (
            <AgxColumn gap="spacing-xl" fill>
              <AgxRow gap="spacing-xl" fill wrap>
                {selectedCampaignDetails.map((detail) => (
                  <AgxColumn key={detail.label} gap="spacing-xs">
                    <AgxTypography
                      id={`update-campaign-status-${detail.label
                        .toLowerCase()
                        .replaceAll(' ', '-')}-label`}
                      type="body_ui"
                      size="small"
                    >
                      {detail.label}
                    </AgxTypography>
                    <AgxTypography
                      id={`update-campaign-status-${detail.label
                        .toLowerCase()
                        .replaceAll(' ', '-')}-value`}
                      type="body"
                      size="medium"
                    >
                      {detail.value}
                    </AgxTypography>
                  </AgxColumn>
                ))}
              </AgxRow>

              <AgxRow gap="spacing-md" alignItems="flex-end" fill wrap>
                <AgxDropdown
                  id="update-campaign-status-stage"
                  label="Status"
                  options={stageOptions}
                  selected={selectedStage}
                  onChange={setSelectedStage}
                  placeholder="Select status"
                  disabled={updateCampaignStatusPending}
                  stretch
                  hideOptionalText
                />

                <AgxButton
                  id="update-campaign-status-save"
                  text={updateCampaignStatusPending ? 'Saving' : 'Save'}
                  variant="primary"
                  leftIcon={<Icon_CheckmarkOutline width={16} height={16} />}
                  disabled={!hasChanged || updateCampaignStatusPending}
                  onClick={() => void saveCampaignStatus()}
                />
              </AgxRow>
            </AgxColumn>
          )}
        </AgxColumn>
      </Panel>
    </AgxColumn>
  );
};

export default UpdateCampaignStatus;
